import {
  calculateItemTotal,
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
  getContainerTypes,
} from "@sellar-io/common";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useMatch } from "react-router";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import {
  Drawer,
  FreeDeliveryEligibleNotice,
  Icon,
  OrderSummaryPrice,
  SpinnerLegacy,
} from "..";
import { CHECKOUT_BUYER_ADD_ON, SUPPLIER } from "../../constants/routes";
import {
  BasketOrderItem,
  ContextOrderBasketType,
  SellarContextActionEnum,
  SidePanelIconTypeEnum,
  useBasketContext,
  useEvents,
  useSellarContext,
  useSellarDistributionEligibility,
} from "../../hooks";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import { useSignupContext } from "../../hooks/useSignupContext.shared";
import { ItemQuantity } from "../../pages/stockList/categoryPage/ItemQuantity";
import { useStockListBasketValue } from "../../pages/stockList/useStockListBasketValue";
import { useStockListContext } from "../../pages/stockList/useStockListContext";
import { useStockListOrderSummaryProps } from "../../pages/stockList/useStockListOrderSummaryProps";
import { Alert } from "../alert/Alert";
import { AvailableFromBadge } from "../badge/AvailableFromBadge";
import { CaseSizeRuleStatus } from "../caseSizeRules/CaseSizeRuleStatus";
import { useMenuDrawerContext } from "../menu/MenuDrawerContextProvider.shared";
import { PriceTierOrderRuleSummaryStorefront } from "../priceTierOrderRuleSummary";
import { Button, H4, H5, H6, SellarIconGradient } from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { StorefrontPromotionBasketLayout } from "../../pages/stockList/categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionBasketLayout";
import { isPromotionalOrderItem } from "../../pages/stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { useLegalEntityCompanyCreditInfo } from "../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { StorefrontBuyerChoiceDeliveryOptionDialog } from "../../pages/stockList/categoryPage/Storefront/StorefrontBuyerChoiceDeliveryOptionDialog/StorefrontBuyerChoiceDeliveryOptionDialog";
import { useStorefrontBuyerChoiceDeliveryOptionDialogEligibility } from "../../pages/stockList/categoryPage/Storefront/StorefrontBuyerChoiceDeliveryOptionDialog/useStorefrontBuyerChoiceDeliveryOptionDialogEligibility";

const formattedOrderItemList = (
  orderItems: ContextOrderBasketType["OrderItems"],
) => {
  return (
    orderItems?.map((item) => {
      return {
        ...item,
        containerType: item.Product.containerType ?? null,
        unit: item.Product.unit ?? null,
        volume: item.Product.volume ?? 0,
        packQuantity: item.Product.packQuantity ?? 0,
      };
    }) ?? []
  );
};

const ItemDeliverySection = ({
  qualifiesForFreeDelivery,
  itemDeliveryCharge,
}: {
  qualifiesForFreeDelivery: boolean;
  itemDeliveryCharge: number;
}) => {
  if (!itemDeliveryCharge) {
    return null;
  }

  if (qualifiesForFreeDelivery || itemDeliveryCharge === 0) {
    return (
      <H5 className={"flex items-center gap-x-0.5 text-green-700"}>
        <Icon icon={"TRUCK_OUTLINE"} size={0.8} />
        <span>|</span>
        <span>FREE</span>
      </H5>
    );
  }

  return (
    <H5 className={"flex items-center gap-x-0.5"}>
      <Icon icon={"TRUCK_OUTLINE"} size={0.8} />
      <span>|</span>
      <span>{formatCurrency(itemDeliveryCharge)}/item</span>
    </H5>
  );
};

const BasketItems = ({
  order,
  orderQualifiesForFreeDelivery,
}: {
  order: ContextOrderBasketType;
  orderQualifiesForFreeDelivery: boolean;
}) => {
  const { basketedProductsQuery } = useStockListContext();

  if (basketedProductsQuery?.isLoading) {
    return <SpinnerLegacy />;
  }

  if (!order.OrderItems) return <div>No basket</div>;
  const orderItems = order.OrderItems[0]?.id
    ? order.OrderItems.sort((a, b) => (a.id && b.id ? a.id - b.id : 0))
    : order.OrderItems;

  const orderItemsWithoutPromotions = orderItems.filter((orderItem) => {
    return !isPromotionalOrderItem(orderItem);
  });

  return (
    <div className="flex flex-col divide-y-2 divide-slate-200">
      {orderItemsWithoutPromotions.map((item, i) => (
        <BasketItem
          key={i}
          item={item}
          hasFreeDelivery={orderQualifiesForFreeDelivery}
        />
      ))}
    </div>
  );
};

const BasketItem = ({
  item,
  hasFreeDelivery,
}: {
  item: BasketOrderItem;
  hasFreeDelivery: boolean;
}) => {
  const [{ order }] = useBasketContext();
  const {
    calculatedDeliveryOffers,
    basketedProductsQuery,
    supplierStorefront,
  } = useStockListContext();

  const basketedProducts = basketedProductsQuery?.data?.pages.flat();

  const basketItem = (basketedProducts || []).find(
    (i) => i.id === item.Product.id,
  );

  const basketItemContainerTypes = getContainerTypes(basketItem?.containerType);

  const itemQualifiesForFreeDelivery = calculatedDeliveryOffers.some(
    (offer) => {
      if (
        basketItemContainerTypes.includes(offer.deliveryRule.containerType) &&
        offer.qualifiesForFreeDelivery
      ) {
        return true;
      }
      return false;
    },
  );

  const itemDeliveryCharge =
    supplierStorefront?.FulfillmentType.FulfillmentTypeContainerDeliveryCharges.find(
      (charge) => {
        return (
          basketItemContainerTypes.includes(charge.containerType) &&
          charge.packQuantity === basketItem?.packQuantity &&
          charge.displayUnit === basketItem.unit &&
          charge.volume === basketItem.volume
        );
      },
    )?.charge ?? 0;

  const hasBeenDeleted = !!basketedProducts && !basketItem;

  const price = item.price;
  const discountable = item.discountable;

  const hasBeenChanged =
    !!basketedProducts &&
    !hasBeenDeleted &&
    basketItem?.ProductPriceTier != null &&
    basketItem.ProductPriceTier.price !== price;

  const isLowOnStock =
    !item.Product.canBackorder && item.quantity > item.Product.availableStock;
  const isOutOfStock =
    !item.Product.canBackorder && item.Product.availableStock <= 0;

  const stockChangeIssue: Array<"outOfStock" | "lowStock"> = [
    isOutOfStock && "outOfStock",
    isLowOnStock && "lowStock",
  ].filter(Boolean);

  const mostImportantStockChangeIssue = stockChangeIssue[0];
  const hasStockChangeIssue = Boolean(mostImportantStockChangeIssue);

  const productName = item.Product.name?.split("•")[0]?.trim();
  const productItem = item.Product.name?.split("•")[1]?.trim();
  const availableFrom = item.Product.availableFrom;

  return (
    <div
      className={twMerge(
        "grid grid-cols-[4fr_1fr] gap-y-2 bg-transparent p-4 transition-colors",
        hasBeenDeleted && "bg-red-50",
        hasBeenChanged && "bg-orange-50",
        hasStockChangeIssue && "bg-orange-50",
      )}
    >
      <div>
        <H5>{productName}</H5>
        <H6>{productItem}</H6>
      </div>
      <H5 className={"flex justify-self-end"}>
        {formatCurrency(price, order?.currency)}
      </H5>
      {!discountable && (
        <H6 className={"col-span-2 text-xs"}>
          This item is not eligible for volume discounts.
        </H6>
      )}
      {hasBeenChanged && (
        <H6 className={"col-span-2 text-xs"}>
          This product's price has been changed from{" "}
          {formatCurrency(price, order?.currency)} to{" "}
          {formatCurrency(price, order?.currency)}.
        </H6>
      )}
      <div className="flex flex-col gap-2">
        {availableFrom && <AvailableFromBadge availableFrom={availableFrom} />}
        <ItemDeliverySection
          qualifiesForFreeDelivery={
            hasFreeDelivery || itemQualifiesForFreeDelivery
          }
          itemDeliveryCharge={itemDeliveryCharge}
        />
      </div>
      <div
        className={twMerge(
          "col-span-2 flex items-center justify-end",
          hasStockChangeIssue && "justify-between",
        )}
      >
        {mostImportantStockChangeIssue === "lowStock" && (
          <span className="font-semibold text-zinc-800">LOW STOCK</span>
        )}
        {mostImportantStockChangeIssue === "outOfStock" && (
          <span className="font-semibold text-zinc-800">OUT OF STOCK</span>
        )}
        <ItemQuantity
          productItem={{
            ...item.Product,
            ProductPriceTier: item.Product.ProductPriceTier
              ? {
                  PriceTierId: item.Product.ProductPriceTier.PriceTierId,
                  price: item.price,
                  fullPrice: item.fullPrice,
                  discountable: item.discountable,
                }
              : null,
          }}
        />
      </div>
      {hasBeenDeleted && (
        <Alert
          className={"col-span-2"}
          title={"This product is no longer available"}
          message={
            "If you want to place this order you must remove the item from your basket."
          }
          type={"warning"}
        />
      )}
    </div>
  );
};

const FlatDeliveryCharge = ({
  flatDeliveryCharge,
}: {
  flatDeliveryCharge: number;
}) => {
  return (
    <H6>
      Delivery charge of {formatCurrency(flatDeliveryCharge)} per order plus any
      applicable per-item delivery charges.
    </H6>
  );
};

export const BasketDrawer = () => {
  const { isMobileDisplayType } = useMenuDrawerContext();
  let [{ order, connection }] = useBasketContext();
  const [searchParams] = useSearchParams();
  const orderFlowToken = searchParams.get("orderFlowToken");

  const [
    buyerChoiceDeliveryOptionDialogActive,
    setBuyerChoiceDeliveryOptionDialogActive,
  ] = useState(false);

  const { supplierStorefront, supplierQuery } = useStockListContext();

  const flatDeliveryCharge = supplierStorefront?.FulfillmentType.charge;

  const supplierIdOrUrlId = useMatch({
    path: SUPPLIER.pattern,
    end: false,
  })?.params.supplierIdOrUrlId;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    apiValues: { price: orderTotal, deliveryCharge, isDeliveryChargeCapped },
    orderApplicableCaseSizeRules,
    partiallySatisfiedCaseSizeRules,
    hasMinimumOrderPriceBeenMet,
    haveMoqRulesBeenSatisfied,
    subTotal,
    discount,
    tax,
    total,
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules:
      supplierStorefront?.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront?.FulfillmentType.charge,
    deliveryChargeCap: supplierStorefront?.FulfillmentType.deliveryChargeCap,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront?.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules:
      supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
      [],
    freeDeliveryBasketSubtotal,
  });

  const [
    { sidePanelActive: basketActive, sidePanelSticky: basketSticky },
    dispatch,
  ] = useSellarContext();

  const { setSignupDialogActive, setBasketTotal } = useSignupContext();
  const basketTotal = useStockListBasketValue();

  const { business } = useSellarCurrentBusiness();

  const navigate = useNavigate();
  const { track } = useEvents();

  const widthLessThan1920 = useMediaQuery("(max-width: 1920px)");

  const location = useLocation();

  const enforceCaseSizeRules = !!supplierStorefront?.CaseSizeRules.enforced;

  useEffect(() => {
    return () => {
      dispatch({
        type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ICON,
        sidePanelIcon: {
          type: SidePanelIconTypeEnum.NONE,
        },
      });
      dispatch({
        type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ACTIVE,
        sidePanelActive: false,
      });
      dispatch({
        type: SellarContextActionEnum.CHANGE_SIDE_PANEL_STICKY,
        sidePanelSticky: false,
      });
    };
  }, [dispatch]);

  useEffect(() => {
    if (order?.OrderItems?.length)
      return dispatch({
        type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ICON,
        sidePanelIcon: {
          type: SidePanelIconTypeEnum.BASKET,
          number: (order.OrderItems || [])
            .map((item) => item.quantity)
            .reduce((a, b) => a + b, 0),
        },
      });

    return dispatch({
      type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ICON,
      sidePanelIcon: {
        type: SidePanelIconTypeEnum.NONE,
      },
    });
  }, [order, dispatch]);

  const setBasketActive = (active: boolean) => {
    dispatch({
      type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ACTIVE,
      sidePanelActive: active,
    });
  };

  const setBasketSticky = (sticky: boolean) => {
    dispatch({
      type: SellarContextActionEnum.CHANGE_SIDE_PANEL_STICKY,
      sidePanelSticky: sticky,
    });
    if (!sticky) setBasketActive(false);
  };

  const isLargeScreenWithOrderItems =
    !widthLessThan1920 && !basketSticky && order?.OrderItems?.length;
  if (isLargeScreenWithOrderItems) {
    setBasketActive(true);
    setBasketSticky(true);
  }

  const isSuccessScreen = location.pathname.includes("success");
  const isSmallScreenAndBasketIsSticky = widthLessThan1920 && basketSticky;
  if (isSmallScreenAndBasketIsSticky || (isSuccessScreen && basketActive)) {
    setBasketActive(false);
    setBasketSticky(false);
  }

  const orderItemsInBasket =
    order?.OrderItems?.reduce((total, item) => {
      return total + item.quantity;
    }, 0) ?? 0;

  const totalItemsInBasket = orderItemsInBasket;

  const orderContainsPreOrderItems = !!findLatestPreOrderAvailableFromDate(
    order?.OrderItems?.map((item) => ({
      availableFrom: item.Product.availableFrom,
    })) ?? [],
  );

  const hasMetOrderConditions = () => {
    if (totalItemsInBasket < 1) {
      return false;
    }

    // if order contains pre order items, skip moq and min price checks

    if (!orderContainsPreOrderItems) {
      if (!haveMoqRulesBeenSatisfied) {
        return false;
      }

      if (!hasMinimumOrderPriceBeenMet) {
        return false;
      }
    }

    // check enforced case size rules have been satisfied

    if (supplierStorefront?.CaseSizeRules.enforced) {
      const unsatisfiedEnforcedCaseSizeRules = partiallySatisfiedCaseSizeRules;
      if (unsatisfiedEnforcedCaseSizeRules.length) {
        return false;
      }
    }

    return true;
  };

  const companyCreditInfoQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: business?.Retailer?.id ?? 0,
    },
    {
      enabled: Boolean(business?.Retailer?.id),
    },
  );

  const companyCreditInfo = companyCreditInfoQuery.data;

  const sellarCreditEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );

  const isImportSupplier =
    !!supplierQuery.data?.onlyAllowCardPaymentWithDeposit;

  const showCreditBalance =
    sellarCreditEnabled &&
    companyCreditInfo?.status === "approved" &&
    companyCreditInfo.userCanViewSellarCreditBalance &&
    companyCreditInfo.userCanAccessSellarCredit &&
    !supplierQuery.data?.hasOptedOutSellarCredit &&
    !isImportSupplier;

  const {
    checkoutSelectedFulfillmentType,
    setCheckoutSelectedFulfillmentType,
  } = useStockListContext();

  const { sessionId } = useMarketStorefrontSessionId();

  const { isEligibleForBuyerChoice } =
    useStorefrontBuyerChoiceDeliveryOptionDialogEligibility({
      supplier: supplierQuery.data,
      directDeliveryArea: supplierStorefront?.directDeliveryArea,
      sdDeliveryArea: supplierStorefront?.sdDeliveryArea,
      checkoutSelectedFulfillmentType,
      connection: supplierStorefront?.SupplierRetailerConnection,
    });

  // Check if SD-only supplier without delivery area - hide checkout
  const { isEligible: isEligibleForSdCheckout } =
    useSellarDistributionEligibility(supplierStorefront);
  const shouldDisableCheckout = !isEligibleForSdCheckout;

  const handleGoToCheckout = () => {
    if (isEligibleForBuyerChoice && !orderFlowToken) {
      setBuyerChoiceDeliveryOptionDialogActive(true);
    } else {
      onSubmit();
    }
  };

  const onSubmit = () => {
    if (!supplierIdOrUrlId) {
      throw new Error("Cannot find supplierIdOrUrlId");
    }

    if (business?.Retailer) {
      track("Checkout Started", {
        retailerName: business.name,
        supplierName: supplierQuery.data?.Business.name,
        retailerId: business.Retailer.id,
        supplierId: supplierQuery.data?.id,
        sessionId,
      });

      return navigate(
        `${CHECKOUT_BUYER_ADD_ON.href({ supplierIdOrUrlId })}${location.search}`,
      );
    }

    // used to display the basket total to user during signup
    setBasketTotal(basketTotal);

    // possible to checkout during sign up flow
    setSignupDialogActive(true);

    // closes the open side basket
    if (!basketSticky) {
      setBasketActive(false);
    }
  };

  return (
    <>
      <Drawer
        sticky={basketSticky}
        setActive={setBasketActive}
        active={basketActive}
        position={"right"}
      >
        {!order ||
        (!supplierStorefront?.FulfillmentType.DeliveryChargeRules &&
          connection) ? (
          <SpinnerLegacy />
        ) : (
          <div
            className={"flex min-h-screen flex-col gap-4 overscroll-contain"}
          >
            <div
              className={twMerge(
                "flex items-center justify-center gap-1 bg-white py-6",
              )}
            >
              <H4>Your order</H4>
              <H6 className={"text-sm"}>({totalItemsInBasket} items)</H6>
            </div>
            {order.id && (
              <div className={"px-4"}>
                <Alert
                  title={`Editing order request #${order.id}`}
                  message={
                    "To cancel your order, use the dropdown menu on the order page."
                  }
                />
              </div>
            )}
            <StorefrontPromotionBasketLayout
              priceTierId={supplierStorefront?.PriceTier?.id}
            />
            <BasketItems
              order={order}
              orderQualifiesForFreeDelivery={deliveryCharge === 0}
            />
            <div
              className={twMerge("mt-auto flex flex-col gap-4 bg-white p-4")}
            >
              {/* basket drawer order summary */}
              <OrderSummaryPrice
                delivery={formatCurrency(deliveryCharge)}
                isDeliveryChargeCapped={isDeliveryChargeCapped}
                subTotal={subTotal}
                discount={discount}
                tax={tax}
                total={total}
              />
              {!!supplierStorefront?.PriceTier?.id && (
                <PriceTierOrderRuleSummaryStorefront
                  orderTotal={orderTotal}
                  orderItemList={formattedOrderItemList(
                    order.OrderItems?.filter(
                      (orderItem) => !isPromotionalOrderItem(orderItem),
                    ),
                  )}
                  priceTierOrderRules={priceTierOrderRules}
                  minimumOrderPrice={minimumOrderPrice}
                  supplierStorefront={supplierStorefront}
                  hideWhenMet={true}
                  hideIfNotApplicable={true}
                  currency={supplierQuery.data?.currency ?? "gbp"}
                />
              )}
              {deliveryCharge !== 0 && !!flatDeliveryCharge && (
                <FlatDeliveryCharge flatDeliveryCharge={flatDeliveryCharge} />
              )}
              {/* always show order applicable case size rules on mobile */}
              <CaseSizeRuleStatus
                caseSizeRules={
                  isMobileDisplayType
                    ? orderApplicableCaseSizeRules
                    : partiallySatisfiedCaseSizeRules
                }
                enforceCaseSizeRules={enforceCaseSizeRules}
              />
              {showCreditBalance && (
                <div className="flex w-full items-center justify-between gap-1 rounded-lg border border-zinc-300 text-sm">
                  <div className="flex items-center gap-2 px-3">
                    <SellarIconGradient />
                    <span>Sellar Credit balance</span>
                  </div>
                  <div className="rounded-r-lg bg-gradient-to-br from-[#B557F1] to-[#D213A6] px-3 py-4">
                    <span className="font-semibold text-white">
                      {formatCurrency(
                        companyCreditInfo.creditAmountAvailable ?? 0,
                      )}
                    </span>
                  </div>
                </div>
              )}
              {!shouldDisableCheckout && (
                <Button
                  className={twMerge(
                    "w-full",
                    (!order.OrderItems?.length ||
                      location.pathname.includes("checkout")) &&
                      "hidden opacity-0",
                  )}
                  onClick={handleGoToCheckout}
                  disabled={!hasMetOrderConditions()}
                >
                  {!connection && business?.type === "retailer"
                    ? "Go to checkout"
                    : "Continue"}
                </Button>
              )}
              {shouldDisableCheckout && (
                <div className="rounded-lg bg-zinc-100 p-4 text-center text-sm text-zinc-600">
                  This supplier only delivers via Sellar Distribution, which is
                  not available in your area.
                </div>
              )}
              {supplierStorefront?.FulfillmentType.DeliveryChargeRules[0] && (
                <FreeDeliveryEligibleNotice
                  freeDeliveryAmount={
                    supplierStorefront.FulfillmentType.DeliveryChargeRules[0]
                      .value
                  }
                  orderSubtotal={
                    order.OrderItems ? calculateItemTotal(order.OrderItems) : 0
                  }
                  comparator={
                    supplierStorefront.FulfillmentType.DeliveryChargeRules[0]
                      .comparator
                  }
                />
              )}
            </div>
          </div>
        )}
      </Drawer>

      {supplierQuery.data && (
        <StorefrontBuyerChoiceDeliveryOptionDialog
          open={buyerChoiceDeliveryOptionDialogActive}
          initialOptionId={null}
          supplier={supplierQuery.data}
          sdDeliveryArea={supplierStorefront?.sdDeliveryArea ?? null}
          directDeliveryArea={supplierStorefront?.directDeliveryArea ?? null}
          onConfirm={(fulfillmentType) => {
            setCheckoutSelectedFulfillmentType(fulfillmentType);
            setBuyerChoiceDeliveryOptionDialogActive(false);
            onSubmit();
          }}
          onDismiss={() => {
            setBuyerChoiceDeliveryOptionDialogActive(false);
          }}
        />
      )}
    </>
  );
};
